import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { ErrorAlert } from '@/components/ui+/ErrorAlert';
import { FullPageSpinner } from '@/components/ui+/FullPageSpinner';
import { InstallPrompt } from '@/components/ui+/InstallPrompt';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { MobilePageWrapper } from '@/components/ui+/MobilePageWrapper';
import { getPaymentAmountCents } from '@/utils/payment';
import { ActiveSessionContent } from './components/ActiveSession';
import { SessionHeader } from './components/SessionHeader';
import { SessionSummaryContent } from './components/SessionSummary';

export const Route = createFileRoute('/s/$sessionUuid/')({
  component: Component,
});

function Component() {
  const { sessionUuid } = Route.useParams();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const {
    data: sessionData,
    isPending: sessionPending,
    isError: sessionError,
    error: sessionErr,
  } = useQuery(api.app.getSessionData, { sessionUuid });
  const { data: site, isPending: sitePending } = useQuery(api.sites.getSiteBySessionUuid, {
    sessionUuid,
  });
  const { data: clientConfig } = useQuery(api.app.getClientConfig);

  const session = sessionData?.session;

  if (sessionPending || sitePending || !clientConfig) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingIcon />
      </div>
    );
  }

  if (sessionError) {
    return <ErrorAlert message={getUserErrorMessage(sessionErr)} />;
  }

  if (!session) {
    return <ErrorAlert message="Session not found." />;
  }

  const step = session.meterReadEndAt !== null ? 'summary' : 'active';

  if (isCheckingOut) {
    return (
      <MobilePageWrapper>
        <FullPageSpinner
          title="Checking out"
          description="Please wait while we finalise your stay."
        />
      </MobilePageWrapper>
    );
  }

  return (
    <MobilePageWrapper>
      <div className="space-y-6">
        <SessionHeader
          companyName={sessionData.companyName}
          locationAddress={sessionData.locationAddress}
          siteCode={site?.siteCode ?? session.siteCode}
        />

        {step === 'summary' && <SessionSummaryContent session={session} />}
        {step === 'active' && (
          <ActiveSessionContent
            session={session}
            site={site ?? null}
            sessionUuid={sessionUuid}
            depositAmountCents={getPaymentAmountCents(clientConfig.worldlineHost)}
            onCheckingOut={setIsCheckingOut}
          />
        )}
      </div>
      <InstallPrompt />
    </MobilePageWrapper>
  );
}
